/** The per-organization single sign-on configuration card. */
const en = {
  title: 'Single sign-on',
  enterprise: 'Enterprise',
  active: 'Active',
  intro:
    'Sign your people in through your own identity provider over OpenID Connect: Microsoft Entra ID, Okta, Keycloak, Auth0, Ping or ADFS. People type their work address on the sign-in screen and are sent to your provider; accounts and memberships can be created on first sign-in, and removing someone in your directory stops them signing in here.',
  gate: 'Single sign-on is part of the Enterprise plan. The form below is read-only until the plan is upgraded.',
  redirectLabel: 'Register this redirect URI (callback URL) in your provider:',
  copy: 'Copy',
  saveFailed: 'Could not save the connection.',
  reached: (issuer: string) => `Reached ${issuer}. Discovery document is valid.`,
  unreachable: 'Could not reach the provider.',
  issuer: 'Issuer URL',
  issuerPlaceholder: 'https://login.microsoftonline.com/<tenant-id>/v2.0',
  issuerHelp:
    'The base URL of your provider. Everything else is read from its discovery document.',
  clientId: 'Client ID',
  clientSecret: 'Client secret',
  secretStored: 'Stored. Leave blank to keep',
  secretStoredHelp: 'A secret is stored. Type a new one to replace it.',
  domains: 'Email domains',
  domainsPlaceholder: 'acme.com, acme.com.tr',
  domainsHelp:
    'Addresses in these domains are routed to your provider. A domain can belong to one organization only.',
  jit: 'Create accounts on first sign-in',
  jitBody:
    'Anyone your provider authenticates gets an account and a membership here. Turn this off to require an invitation first.',
  defaultRole: 'Role for new accounts',
  roles: { VIEWER: 'Viewer', MEMBER: 'Member', ADMIN: 'Admin' },
  enforce: 'Require SSO for these domains',
  enforceBody:
    'Password sign-in is refused for everyone in the domains above, so nobody can sidestep your MFA and conditional-access rules with an old password. Turn this on only once a sign-in has worked, including for yourself.',
  enabled: 'Connection enabled',
  saving: 'Saving…',
  save: 'Save connection',
  testing: 'Checking…',
  testProvider: 'Test provider',
  remove: 'Remove',
  lastLogin: (when: string) => `Last successful sign-in ${when}.`,
  noLogin: 'No successful sign-in through this connection yet.',
  discovered: (when: string) => ` Discovery last read ${when}.`,

  protocol: 'Protocol',
  oidc: 'OpenID Connect',
  oidcHint: 'Entra ID, Okta, Keycloak, Auth0, Ping, ADFS. Configure one issuer; everything else is discovered.',
  saml: 'SAML 2.0',
  samlHint: 'For an IdP that offers nothing else, or procurement that asks for it by name. No discovery. Endpoints and certificates are configured explicitly.',
  samlSp: 'Give your identity provider these',
  samlEntityId: 'Entity ID (Audience)',
  samlAcs: 'Assertion consumer service (Reply URL)',
  samlSpSlo: 'Single logout service (our endpoint)',
  samlMetadata: 'Our metadata',
  samlIdpEntityId: 'Identity provider entity ID',
  samlIdpEntityIdHint: 'The Issuer in every assertion it signs.',
  samlSsoUrl: 'Sign-on URL',
  samlSloUrl: 'Single logout URL (optional)',
  samlCertificates: 'Signing certificate(s)',
  samlCertificatesHint:
    'Paste the base64 certificate from the IdP metadata. More than one is fine. During a rotation both are accepted. Leave blank to keep what is stored.',
  samlStored: (n: number) => `${n} certificate(s) stored`,
  samlAttributes: 'Attribute names',
  samlAttributesHint:
    'Leave blank unless your IdP uses custom claim names. The usual Entra and Okta ones are recognised.',
  samlEmailAttribute: 'Email attribute',
  samlNameAttribute: 'Name attribute',
  samlGroupsAttribute: 'Groups attribute',
  samlSignRequests: 'Sign our authentication requests',
  samlSignRequestsHint: 'Required by some identity providers; most do not ask for it.',

  groups: 'Directory groups → roles',
  groupsHint:
    'A group the IdP sends grants the role here. Re-applied on every sign-in and every SCIM push, so a change in the directory takes effect rather than drifting. An Owner is never changed this way.',
  groupName: 'Group',
  groupRole: 'Role',
  addGroup: 'Add mapping',
  saveGroups: 'Save mappings',

  scim: 'SCIM provisioning',
  scimHint:
    'Your directory pushes users and deactivations here, so removing someone there removes their access. Users, PATCH and filtering are supported; group membership maps to roles through the table above.',
  scimUrl: 'Tenant URL',
  scimToken: 'Secret token',
  scimGenerate: 'Generate token',
  scimRegenerate: 'Regenerate token',
  scimTokenOnce:
    'Copy it now. It is stored only as a hash and will not be shown again.',
  scimActive: (prefix: string) => `A token starting ${prefix}… is active.`,
  scimNoToken: 'No token yet.',
  scimLastSync: (when: string) => `Last directory call ${when}.`,
  scimNeverSynced: 'Your directory has not called this endpoint yet.',
};

const tr: typeof en = {
  title: 'Tek oturum açma',
  enterprise: 'Enterprise',
  active: 'Etkin',
  intro:
    'Ekibinizi OpenID Connect üzerinden kendi kimlik sağlayıcınızla içeri alın: Microsoft Entra ID, Okta, Keycloak, Auth0, Ping ya da ADFS. Kişiler giriş ekranında kurumsal adreslerini yazar ve sağlayıcınıza yönlendirilir; hesaplar ve üyelikler ilk girişte oluşturulabilir, dizininizden birini kaldırdığınızda buraya da giremez.',
  gate: 'Tek oturum açma Enterprise planına dahildir. Plan yükseltilene kadar aşağıdaki form salt okunurdur.',
  redirectLabel: 'Bu yönlendirme URI’sini (callback URL) sağlayıcınıza kaydedin:',
  copy: 'Kopyala',
  saveFailed: 'Bağlantı kaydedilemedi.',
  reached: (issuer: string) => `${issuer} adresine ulaşıldı, discovery belgesi geçerli.`,
  unreachable: 'Sağlayıcıya ulaşılamadı.',
  issuer: 'Issuer URL',
  issuerPlaceholder: 'https://login.microsoftonline.com/<kiracı-id>/v2.0',
  issuerHelp:
    'Sağlayıcınızın temel URL’si. Geri kalan her şey discovery belgesinden okunur.',
  clientId: 'İstemci kimliği',
  clientSecret: 'İstemci gizli anahtarı',
  secretStored: 'Saklı. Korumak için boş bırakın',
  secretStoredHelp: 'Bir gizli anahtar saklı. Değiştirmek için yenisini yazın.',
  domains: 'E-posta alan adları',
  domainsPlaceholder: 'acme.com, acme.com.tr',
  domainsHelp:
    'Bu alan adlarındaki adresler sağlayıcınıza yönlendirilir. Bir alan adı yalnızca tek bir organizasyona ait olabilir.',
  jit: 'İlk girişte hesap oluştur',
  jitBody:
    'Sağlayıcınızın doğruladığı herkes burada bir hesap ve üyelik alır. Önce davet gerektirmek için kapatın.',
  defaultRole: 'Yeni hesapların rolü',
  roles: { VIEWER: 'İzleyici', MEMBER: 'Üye', ADMIN: 'Yönetici' },
  enforce: 'Bu alan adları için SSO zorunlu kıl',
  enforceBody:
    'Yukarıdaki alan adlarındaki herkes için parola girişi reddedilir; böylece kimse eski bir parolayla MFA ve koşullu erişim kurallarınızı atlayamaz. Bunu ancak bir giriş başarıyla gerçekleştikten sonra açın. Kendi girişiniz dâhil.',
  enabled: 'Bağlantı etkin',
  saving: 'Kaydediliyor…',
  save: 'Bağlantıyı kaydet',
  testing: 'Kontrol ediliyor…',
  testProvider: 'Sağlayıcıyı test et',
  remove: 'Kaldır',
  lastLogin: (when: string) => `Son başarılı giriş ${when}.`,
  noLogin: 'Bu bağlantı üzerinden henüz başarılı bir giriş olmadı.',
  discovered: (when: string) => ` Discovery en son ${when} okundu.`,

  protocol: 'Protokol',
  oidc: 'OpenID Connect',
  oidcHint:
    'Entra ID, Okta, Keycloak, Auth0, Ping, ADFS. Tek bir issuer girin; gerisi keşifle bulunur.',
  saml: 'SAML 2.0',
  samlHint:
    'Başka bir şey sunmayan bir IdP için ya da satın alma süreci adıyla istediğinde. Keşif yok. Uç noktalar ve sertifikalar açıkça girilir.',
  samlSp: 'Kimlik sağlayıcınıza bunları verin',
  samlEntityId: 'Entity ID (Audience)',
  samlAcs: 'Assertion consumer service (Reply URL)',
  samlSpSlo: 'Tek çıkış servisi (bizim uç noktamız)',
  samlMetadata: 'Metadata’mız',
  samlIdpEntityId: 'Kimlik sağlayıcı entity ID',
  samlIdpEntityIdHint: 'İmzaladığı her assertion’daki Issuer değeri.',
  samlSsoUrl: 'Oturum açma URL’si',
  samlSloUrl: 'Tek çıkış URL’si (isteğe bağlı)',
  samlCertificates: 'İmzalama sertifikası / sertifikaları',
  samlCertificatesHint:
    'IdP metadata’sındaki base64 sertifikayı yapıştırın. Birden fazla olabilir. Anahtar değişimi sırasında ikisi de kabul edilir. Saklanan sertifikayı korumak için boş bırakın.',
  samlStored: (n: number) => `${n} sertifika saklı`,
  samlAttributes: 'Öznitelik adları',
  samlAttributesHint:
    'IdP’niz özel claim adları kullanmıyorsa boş bırakın. Entra ve Okta’nın alışıldık adları tanınıyor.',
  samlEmailAttribute: 'E-posta özniteliği',
  samlNameAttribute: 'Ad özniteliği',
  samlGroupsAttribute: 'Gruplar özniteliği',
  samlSignRequests: 'Kimlik doğrulama isteklerimizi imzala',
  samlSignRequestsHint: 'Bazı kimlik sağlayıcılar ister; çoğu istemez.',

  groups: 'Dizin grupları → roller',
  groupsHint:
    'IdP’nin gönderdiği bir grup buradaki rolü verir. Her girişte ve her SCIM aktarımında yeniden uygulanır; böylece dizindeki değişiklik kayarak değil, doğrudan etkili olur. Sahip rolü bu yolla asla değişmez.',
  groupName: 'Grup',
  groupRole: 'Rol',
  addGroup: 'Eşleme ekle',
  saveGroups: 'Eşlemeleri kaydet',

  scim: 'SCIM ile hazırlama',
  scimHint:
    'Dizininiz kullanıcıları ve devre dışı bırakmaları buraya gönderir; orada birini kaldırmak buradaki erişimini de kaldırır. Kullanıcılar, PATCH ve filtreleme destekleniyor; grup üyeliği yukarıdaki tabloyla rollere eşlenir.',
  scimUrl: 'Kiracı URL’si',
  scimToken: 'Gizli belirteç',
  scimGenerate: 'Belirteç üret',
  scimRegenerate: 'Belirteci yenile',
  scimTokenOnce:
    'Şimdi kopyalayın. Yalnızca karması saklanır ve bir daha gösterilmez.',
  scimActive: (prefix: string) => `${prefix}… ile başlayan bir belirteç etkin.`,
  scimNoToken: 'Henüz belirteç yok.',
  scimLastSync: (when: string) => `Dizinden son çağrı ${when}.`,
  scimNeverSynced: 'Dizininiz bu uç noktayı henüz çağırmadı.',
};

export const sso = { en, tr };
